import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Link,
  Stack,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Language as LanguageIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth, SsoProvider } from '../contexts/AuthContext';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { useApplication, ApplicationName } from '../contexts/ApplicationContext';
import { getApplicationVersion } from '../config/applicationsConfig';
import Logo from '../components/Logo';
import { ZoomField } from '../components/ZoomField';
import { ZoomData } from '../services/blobticZoomService';


import ForgotPasswordDialog from '../components/ForgotPasswordDialog';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { DoubleAuthDialog } from '../components/DoubleAuthDialog';


// Icônes officielles vectorielles pour les fournisseurs SSO
const GoogleSvgIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

const MicrosoftSvgIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const LoginPage: React.FC = () => {

  const { login, error, clearError, isLoading, requiresDoubleAuth, getSsoProviders, getSsoAuthorizeUrl } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { currentApp, isValidApp } = useApplication();
  const { appName: routeAppName } = useParams<{ appName?: string }>();
  const effectiveApp: ApplicationName = (routeAppName && isValidApp(routeAppName) ? (routeAppName as ApplicationName) : currentApp) || 'Planning';

  const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([]);
  const [ssoLoading, setSsoLoading] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    agency: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedAgency, setSelectedAgency] = useState<ZoomData | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);

  // Chargement des fournisseurs SSO configurés
  useEffect(() => {
    getSsoProviders().then((providers) => {
      if (Array.isArray(providers)) {
        setSsoProviders(providers);
      }
    }).catch(() => {});
  }, []);

  // Déclenchement du flux SSO
  const handleSsoClick = async (providerId: string) => {
    try {
      setSsoLoading(providerId);
      const targetApp = effectiveApp;
      const authUrl = await getSsoAuthorizeUrl(providerId, targetApp, formData.agency);
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setSsoLoading(null);
      }
    } catch (e) {
      setSsoLoading(null);
    }
  };

  // Effacement des erreurs lors du changement des champs
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [formData, clearError]);

  // Validation des champs
  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'email':
        if (!value) return t('error.email.required');
        if (!/\S+@\S+\.\S+/.test(value)) return t('error.email.invalid');
        return '';
      case 'password':
        if (!value) return t('error.password.required');
        if (value.length < 6) return t('error.password.minLength');
        return '';
      case 'agency':
        if (!value) return t('error.agency.required');
        if (value.length < 3) return t('error.agency.minLength');
        return '';
      default:
        return '';
    }
  };

  // Gestion des changements de champs
  const handleFieldChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    // Validation en temps réel
    const error = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Gestion du changement d'agence via zoom
  const handleAgencyChange = (value: string, selectedData?: ZoomData) => {
    setFormData(prev => ({ ...prev, agency: value }));
    setSelectedAgency(selectedData || null);

    // Validation en temps réel
    const error = validateField('agency', value);
    setFormErrors(prev => ({
      ...prev,
      agency: error
    }));
  };



  // Gestion du changement de langue
  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageSelect = (selectedLanguage: Language) => {
    setLanguage(selectedLanguage);
    handleLanguageMenuClose();
  };

  // Gestion de la soumission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation complète
    const errors: Record<string, string> = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) errors[key] = error;
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Tentative de connexion
    const success = await login({
      ...formData,
      applicationName: effectiveApp,
      language: language
    });
    if (success) {
      // La redirection sera gérée par le contexte d'authentification
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >



      <Container component="main" maxWidth="sm" sx={{ zIndex: 1, position: 'relative' }}>
        <Box
          sx={{
            minHeight: { xs: '100dvh', sm: '100vh' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: { xs: 2, sm: 4 },
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {/* Logo */}
          <Box sx={{
            textAlign: 'center',
            mb: { xs: 2, sm: 4 },
            animation: 'fadeInDown 0.6s ease-out',
            '@keyframes fadeInDown': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-20px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}>
            <Logo
              variant="primary"
              height={100}
              sx={{
                mb: 1.25,
                height: { xs: 80, sm: 100 }
              }}
            />
          </Box>

          {/* Formulaire de connexion */}
          <Paper
            elevation={3}
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              width: '100%',
              maxWidth: 450,
              background: (theme) => theme.palette.background.paper,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              transition: 'all 0.3s ease-in-out',
              animation: 'fadeInUp 0.8s ease-out',
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Card sx={{ borderRadius: 0, background: 'transparent' }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                {/* Titre et icône de langue */}
                <Box sx={{ position: 'relative', mb: 5 }}>
                  <Typography
                    component="h2"
                    variant="h3"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 800,
                      color: 'primary.main',
                      letterSpacing: '-0.5px',
                      mb: 1,
                    }}
                  >
                    {t('login.title')}
                  </Typography>
                  {effectiveApp && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mt: -1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: 'center',
                          color: 'primary.main',
                          fontWeight: 600,
                        }}
                      >
                        {effectiveApp}
                      </Typography>
                      <Chip
                        label={getApplicationVersion(effectiveApp)}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(37, 99, 235, 0.08)',
                          color: 'primary.main',
                          border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(37, 99, 235, 0.2)'}`,
                          borderRadius: '9999px',
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <IconButton
                      onClick={handleLanguageMenuOpen}
                      sx={{
                        color: 'primary.main',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(26, 35, 126, 0.1)',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <LanguageIcon />
                    </IconButton>

                  </Box>

                  {/* Menu de sélection de langue */}
                  <Menu
                    anchorEl={languageMenuAnchor}
                    open={Boolean(languageMenuAnchor)}
                    onClose={handleLanguageMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    sx={{
                      '& .MuiPaper-root': {
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        minWidth: 140,
                        border: '1px solid rgba(26, 35, 126, 0.1)',
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => handleLanguageSelect('fr')}
                      selected={language === 'fr'}
                      sx={{
                        color: language === 'fr' ? '#1a237e' : 'inherit',
                        fontWeight: language === 'fr' ? 600 : 400,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(26, 35, 126, 0.08)',
                        },
                      }}
                    >
                      Français
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleLanguageSelect('en')}
                      selected={language === 'en'}
                      sx={{
                        color: language === 'en' ? '#1a237e' : 'inherit',
                        fontWeight: language === 'en' ? 600 : 400,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(26, 35, 126, 0.08)',
                        },
                      }}
                    >
                      English
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleLanguageSelect('ar')}
                      selected={language === 'ar'}
                      sx={{
                        color: language === 'ar' ? '#1a237e' : 'inherit',
                        fontWeight: language === 'ar' ? 600 : 400,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(26, 35, 126, 0.08)',
                        },
                      }}
                    >
                      العربية
                    </MenuItem>
                  </Menu>
                </Box>



                {/* Affichage des erreurs */}
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 4,
                      borderRadius: '12px',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.2)',
                      animation: 'shake 0.5s ease-in-out',
                      '@keyframes shake': {
                        '0%, 100%': { transform: 'translateX(0)' },
                        '25%': { transform: 'translateX(-5px)' },
                        '75%': { transform: 'translateX(5px)' },
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  {/* Champ Email */}
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t('login.email')}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      transition: 'all 0.3s ease-in-out',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                          boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}1A`,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                    }}
                  />

                  {/* Champ Mot de passe */}
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t('login.password')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ marginInlineEnd: 1, marginInlineStart: 0.5 }}>
                          <LockIcon color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      transition: 'all 0.3s ease-in-out',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: '2px',
                          boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}1A`, // 10% opacity
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                    }}
                  />

                  {/* Champ Agence avec Zoom */}
                  <ZoomField
                    zoomKey="dossier"
                    label={t('login.agency')}
                    placeholder={t('zoom.agency.placeholder')}
                    value={formData.agency}
                    onChange={handleAgencyChange}
                    displayField="DOS_CODE"
                    valueField="DOS_CODE"
                    required
                    fullWidth
                    dynamicParams={{
                      USER_MAIL: formData.email,
                      USER_PASS: formData.password
                    }}
                    showSelectedInfo={false}
                    helperText={formErrors.agency}
                    error={!!formErrors.agency}
                    zoomDisabled={!formData.email || !formData.password}
                    zoomDisabledMessage={t('zoom.agency.disabled')}
                    sx={{ mb: 3, mt: 1 }}
                  />

                  {/* Liens de gestion des mots de passe */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Stack direction="row" spacing={6} justifyContent="space-around">
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowChangePassword(true);
                        }}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.dark',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        {/* Changer le mot de passe */}
                        {t('btn.changePassword')}
                      </Link>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowForgotPassword(true);
                        }}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.dark',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        {t('login.forgotPassword')}
                      </Link>
                    </Stack>
                  </Box>

                  {/* Bouton de connexion */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{
                      py: 2.5,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      borderRadius: '16px',
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: (theme) => `0 8px 24px ${theme.palette.primary.main}4D`, // 30% opacity
                      transition: 'all 0.3s ease-in-out',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      '&:hover': {
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                        transform: 'translateY(-3px)',
                        boxShadow: (theme) => `0 12px 32px ${theme.palette.primary.main}66`, // 40% opacity
                      },
                      '&:active': {
                        transform: 'translateY(-1px)',
                      },
                      '&:disabled': {
                        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                        color: (theme) => theme.palette.text.disabled,
                        transform: 'none',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('login.connect')
                    )}
                  </Button>

                  {/* Section SSO dynamique */}
                  {ssoProviders.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                        <Divider sx={{ flexGrow: 1 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2,
                            color: 'text.secondary',
                            fontWeight: 700,
                            letterSpacing: '0.8px',
                            fontSize: '0.75rem',
                          }}
                        >
                          {t('login.sso.or')}
                        </Typography>
                        <Divider sx={{ flexGrow: 1 }} />
                      </Box>

                      <Stack spacing={1.5}>
                        {ssoProviders.map((provider) => {
                          const isGoogle = provider.id === 'google';
                          const isMicrosoft = provider.id === 'microsoft';
                          const label = isGoogle
                            ? t('login.sso.google')
                            : isMicrosoft
                            ? t('login.sso.microsoft')
                            : t('login.sso.oidc', provider.name);

                          return (
                            <Button
                              key={provider.id}
                              fullWidth
                              variant="outlined"
                              size="large"
                              disabled={isLoading || !!ssoLoading}
                              onClick={() => handleSsoClick(provider.id)}
                              startIcon={
                                ssoLoading === provider.id ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : isGoogle ? (
                                  <GoogleSvgIcon />
                                ) : isMicrosoft ? (
                                  <MicrosoftSvgIcon />
                                ) : (
                                  <SecurityIcon />
                                )
                              }
                              sx={{
                                py: 1.5,
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                borderRadius: '12px',
                                textTransform: 'none',
                                borderColor: (theme) =>
                                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                                color: 'text.primary',
                                bgcolor: (theme) =>
                                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#ffffff',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  borderColor: isGoogle ? '#4285F4' : isMicrosoft ? '#00a4ef' : 'primary.main',
                                  bgcolor: (theme) =>
                                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.02)',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                },
                              }}
                            >
                              {label}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Paper>

          {/* Informations supplémentaires */}
          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontWeight: 400,
                opacity: 0.8,
                transition: 'opacity 0.3s ease-in-out',
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              {t('login.copyright')}
            </Typography>
          </Box>
        </Box>
      </Container>



      {/* Dialog mot de passe oublié */}
      <ForgotPasswordDialog
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />

      {/* Dialog changement de mot de passe */}
      <ChangePasswordDialog
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />

      {/* Dialogue de double authentification */}
      <DoubleAuthDialog
        open={requiresDoubleAuth}
        onClose={() => { }} // La fermeture est gérée par le contexte d'authentification
      />
    </Box>
  );
};

export default LoginPage;
